import React, { useRef, useState } from "react";
import { Extension, Mark, markInputRule, markPasteRule, mergeAttributes } from "@tiptap/core";
import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, Color, BackgroundColor } from "@tiptap/extension-text-style";
import { API_ORIGIN, translateAuthError } from "../api/authApi";
import { uploadContentImage } from "../api/uploadsApi";

// The site's established brand accent (--copper in App.css) — swap this
// if you want the toolbar's "رنگ برند" button to apply a different exact
// hex instead.
const BRAND_COLOR = "#7d1826";
const HIGHLIGHT_BACKGROUND = "#000000";

// Admins often paste text that already has bracket-style footnote markers
// ("[1]", "[۲]") — this turns them into a small superscript number
// attached to the preceding word (see App.css's sup.footnote-ref) the
// moment they're typed or pasted, instead of admins having to notice and
// reformat them by hand. RichText.js does the same conversion for
// already-published content that predates this feature.
const FOOTNOTE_REF_INPUT_REGEX = /\[([0-9۰-۹]+)\]$/;
const FOOTNOTE_REF_PASTE_REGEX = /\[([0-9۰-۹]+)\]/g;

const FootnoteRef = Mark.create({
  name: "footnoteRef",
  inclusive: false,
  parseHTML() {
    return [{ tag: "sup.footnote-ref" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["sup", mergeAttributes(HTMLAttributes, { class: "footnote-ref" }), 0];
  },
  addInputRules() {
    return [markInputRule({ find: FOOTNOTE_REF_INPUT_REGEX, type: this.type })];
  },
  addPasteRules() {
    return [markPasteRule({ find: FOOTNOTE_REF_PASTE_REGEX, type: this.type })];
  },
});

// The site is RTL end to end (see public/index.html's dir="rtl"), but
// admins sometimes write whole paragraphs/headings in English — this adds
// a `dir` attribute to the block types that can hold text, saved as the
// plain HTML `dir` attribute so the browser (and RichText.js's DOMPurify,
// which allows it by default) handles left-to-right flow natively, no
// extra CSS needed.
const BLOCK_TYPES_WITH_DIRECTION = ["paragraph", "heading", "blockquote", "listItem"];

// Paragraph is checked first since it's the innermost match even when the
// cursor is inside a blockquote (blockquote's content is itself made of
// paragraph nodes) — that's what we want, so the `dir` toggle affects
// just that line rather than clobbering the whole quote.
//
// listItem is the one exception, checked before paragraph instead of
// after: a bullet/numbered list item's own text also lives in a nested
// paragraph, but the marker's side (left vs. right) follows the <li>
// element's own dir, not its inner paragraph's — setting dir on the
// paragraph would reorder the text but leave the bullet stuck on the
// wrong side. Targeting listItem itself fixes both at once.
function activeDirectionBlockType(editor) {
  if (editor.isActive("listItem")) return "listItem";
  return BLOCK_TYPES_WITH_DIRECTION.find((type) => type !== "listItem" && editor.isActive(type)) || "paragraph";
}

const TextDirection = Extension.create({
  name: "textDirection",
  addGlobalAttributes() {
    return [
      {
        types: BLOCK_TYPES_WITH_DIRECTION,
        attributes: {
          dir: {
            default: null,
            parseHTML: (element) => element.getAttribute("dir"),
            renderHTML: (attributes) => (attributes.dir ? { dir: attributes.dir } : {}),
          },
        },
      },
    ];
  },
});

// Browsers treat Tab as "move focus to the next element" by default, and
// neither ProseMirror's base keymap nor StarterKit bind anything to a
// plain Tab press — only @tiptap/extension-list's bundled keymap does,
// and only for sinking/lifting a list item. So outside a list, Tab was
// jumping focus out of the editor entirely instead of doing anything
// editor-related. This inserts a typewriter-style indent (four
// non-breaking spaces, so it survives DOMPurify/whitespace collapsing as
// plain text with no new schema/CSS needed) at the cursor, and
// Shift-Tab removes one if the text immediately before the cursor is
// exactly that run.
//
// Deliberately defers (returns false) whenever the cursor is inside a
// list item, regardless of extension ordering, so list-indent (Tab) and
// list-outdent (Shift-Tab) — @tiptap/extension-list's own job — are
// untouched.
const TAB_INDENT = "    ";

const TabIndent = Extension.create({
  name: "tabIndent",
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (this.editor.isActive("listItem")) return false;
        return this.editor.commands.insertContent(TAB_INDENT);
      },
      "Shift-Tab": () => {
        if (this.editor.isActive("listItem")) return false;
        const { from } = this.editor.state.selection;
        const start = Math.max(0, from - TAB_INDENT.length);
        const textBefore = this.editor.state.doc.textBetween(start, from, "\n", "\n");
        if (textBefore === TAB_INDENT) {
          return this.editor.commands.deleteRange({ from: start, to: from });
        }
        if (textBefore.endsWith(" ")) {
          return this.editor.commands.deleteRange({ from: from - 1, to: from });
        }
        return false;
      },
    };
  },
});

// Adds a left/center/right `align` attribute and an optional `caption`
// to the stock image node, stored as `data-align`/`data-caption` in the
// saved HTML so RichText.js can float/center the image and render a
// small subtitle underneath it on the public page.
const AlignableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align") || "center",
        renderHTML: (attributes) => ({ "data-align": attributes.align }),
      },
      caption: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-caption") || null,
        renderHTML: (attributes) => (attributes.caption ? { "data-caption": attributes.caption } : {}),
      },
    };
  },
});

// Admin-only WYSIWYG editor for content fields (issue/publication topic
// body, article body). Emits sanitized-on-render HTML (see RichText.js)
// so admins can format text — and place images — without touching
// markup themselves.
function ToolbarButton({ onClick, active, disabled, label, children }) {
  return (
    <button
      type="button"
      className={"rich-text-toolbar-btn" + (active ? " rich-text-toolbar-btn--active" : "")}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      {children}
    </button>
  );
}

function RichTextEditor({ id, value, onChange }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const token = localStorage.getItem("token");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
      AlignableImage.configure({ inline: false }),
      TextDirection,
      TabIndent,
      FootnoteRef,
      TextAlign.configure({
        // listItem alongside its nested paragraph: setTextAlign updates
        // every matching type under the selection, so this sets
        // text-align on the <li> itself (not just its inner paragraph) —
        // needed because list markers (see App.css's list-style-position
        // handling) key off the <li>'s own box, not a descendant's.
        types: ["paragraph", "heading", "blockquote", "listItem"],
        alignments: ["left", "center", "right", "justify"],
      }),
      TextStyle,
      Color,
      BackgroundColor,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Re-derives the toolbar's active/enabled state on every editor
  // transaction (typing, but also clicking into an image or moving the
  // cursor) — without this the image-align buttons would only ever
  // reflect whichever node was active the last time a keystroke fired.
  const toolbarState = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor) return null;
      return {
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        strike: editor.isActive("strike"),
        h2: editor.isActive("heading", { level: 2 }),
        h3: editor.isActive("heading", { level: 3 }),
        bulletList: editor.isActive("bulletList"),
        orderedList: editor.isActive("orderedList"),
        blockquote: editor.isActive("blockquote"),
        link: editor.isActive("link"),
        image: editor.isActive("image"),
        imageAlign: editor.getAttributes("image").align,
        imageCaption: editor.getAttributes("image").caption,
        dir: editor.getAttributes(activeDirectionBlockType(editor)).dir,
        // No explicit textAlign attribute (the common case) means the
        // browser default applies — "start", which in this RTL site
        // resolves to right, not left. Fall back to "right" so the
        // toolbar highlights the button that actually matches what's
        // rendered, not whichever direction a non-RTL site would default to.
        textAlign: ["left", "center", "right", "justify"].find((align) => editor.isActive({ textAlign: align })) || "right",
        brandColor: editor.isActive("textStyle", { color: BRAND_COLOR }),
        blackHighlight: editor.isActive("textStyle", { backgroundColor: HIGHLIGHT_BACKGROUND }),
        canUndo: editor.can().undo(),
        canRedo: editor.can().redo(),
      };
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML() && !(value === "" && editor.getHTML() === "<p></p>")) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor || !toolbarState) return null;

  function setLink() {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("آدرس لینک را وارد کنید", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }

  async function handleImageChosen(event) {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    setUploadError("");
    try {
      const { url } = await uploadContentImage(token, file);
      // After inserting a block image, ProseMirror leaves it as a "node
      // selection" (images have no text content to place a cursor in).
      // Inserting a second image right after would then replace that
      // selection — silently deleting the first image — so createParagraphNear
      // opens an adjacent paragraph and moves the cursor into it first.
      editor
        .chain()
        .focus()
        .setImage({ src: `${API_ORIGIN}${url}`, align: "center" })
        .createParagraphNear()
        .run();
    } catch (err) {
      setUploadError(translateAuthError(err.message));
    } finally {
      setUploading(false);
    }
  }

  function setImageAlign(align) {
    editor.chain().focus().updateAttributes("image", { align }).run();
  }

  function setImageCaption() {
    const previous = editor.getAttributes("image").caption || "";
    const caption = window.prompt("زیرنویس تصویر را وارد کنید (برای حذف، خالی بگذارید)", previous);
    if (caption === null) return;
    editor
      .chain()
      .focus()
      .updateAttributes("image", { caption: caption.trim() || null })
      .run();
  }

  function toggleDirection() {
    const type = activeDirectionBlockType(editor);
    const isLtr = editor.getAttributes(type).dir === "ltr";
    editor
      .chain()
      .focus()
      .updateAttributes(type, { dir: isLtr ? null : "ltr" })
      .run();
  }

  // Applies/removes BRAND_COLOR as the selected text's color — renders as
  // <span style="color: #7d1826"> via the textStyle mark.
  function toggleBrandColor() {
    if (editor.isActive("textStyle", { color: BRAND_COLOR })) {
      editor.chain().focus().unsetColor().run();
    } else {
      editor.chain().focus().setColor(BRAND_COLOR).run();
    }
  }

  // Applies/removes a black background + white text together — both are
  // attributes on the same textStyle mark, so they render as one
  // <span style="color: #fff; background-color: #000">, not a nested tag.
  // Black-on-black — the text is genuinely hidden (a redaction/spoiler
  // effect), not white-on-black. A reader can still reveal it by
  // selecting it with the mouse, since the site's ::selection style
  // (copper background, white text) overrides the inline color while
  // selected.
  function toggleBlackHighlight() {
    if (editor.isActive("textStyle", { backgroundColor: HIGHLIGHT_BACKGROUND })) {
      editor.chain().focus().unsetBackgroundColor().unsetColor().run();
    } else {
      editor.chain().focus().setBackgroundColor(HIGHLIGHT_BACKGROUND).setColor(HIGHLIGHT_BACKGROUND).run();
    }
  }

  return (
    <div className="rich-text-editor" id={id}>
      <div className="rich-text-toolbar">
        <ToolbarButton
          label="ضخیم"
          active={toolbarState.bold}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </ToolbarButton>
        <ToolbarButton
          label="مورب"
          active={toolbarState.italic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          I
        </ToolbarButton>
        <ToolbarButton
          label="خط‌خورده"
          active={toolbarState.strike}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          S
        </ToolbarButton>
        <ToolbarButton
          label="رنگ برند"
          active={toolbarState.brandColor}
          onClick={toggleBrandColor}
        >
          {/* When the button is active, .rich-text-toolbar-btn--active
              turns its own background copper — the same color as this
              preview letter, which would make it disappear. White keeps
              it visible against that background; otherwise show the
              actual brand color as a preview of what clicking applies. */}
          <span style={{ color: toolbarState.brandColor ? "#fff" : BRAND_COLOR }}>A</span>
        </ToolbarButton>
        <ToolbarButton
          label="پنهان‌کردن متن (هایلایت مشکی)"
          active={toolbarState.blackHighlight}
          onClick={toggleBlackHighlight}
        >
          {/* A solid bar, not a letter — this hides text (black-on-black),
              so a redaction-bar icon reads more clearly than an "A" would. */}
          <span style={{ display: "inline-block", width: "1em", height: "0.7em", background: HIGHLIGHT_BACKGROUND, borderRadius: "1px" }} />
        </ToolbarButton>
        <span className="rich-text-toolbar-sep" />
        <ToolbarButton
          label="تیتر"
          active={toolbarState.h2}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          label="زیرتیتر"
          active={toolbarState.h3}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarButton>
        <span className="rich-text-toolbar-sep" />
        <ToolbarButton
          label="فهرست نقطه‌ای"
          active={toolbarState.bulletList}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          •—
        </ToolbarButton>
        <ToolbarButton
          label="فهرست شماره‌دار"
          active={toolbarState.orderedList}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1.
        </ToolbarButton>
        <ToolbarButton
          label="نقل‌قول"
          active={toolbarState.blockquote}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          ❝
        </ToolbarButton>
        <ToolbarButton label="پیوند" active={toolbarState.link} onClick={setLink}>
          🔗
        </ToolbarButton>
        <span className="rich-text-toolbar-sep" />
        <ToolbarButton
          label="تراز راست"
          active={toolbarState.textAlign === "right"}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        >
          راست
        </ToolbarButton>
        <ToolbarButton
          label="وسط‌چین"
          active={toolbarState.textAlign === "center"}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          وسط
        </ToolbarButton>
        <ToolbarButton
          label="تراز چپ"
          active={toolbarState.textAlign === "left"}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        >
          چپ
        </ToolbarButton>
        <ToolbarButton
          label="تراز دوطرفه (جاستیفای)"
          active={toolbarState.textAlign === "justify"}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        >
          تمام
        </ToolbarButton>
        <span className="rich-text-toolbar-sep" />
        <ToolbarButton
          label="جهت چپ‌به‌راست برای متن انگلیسی"
          active={toolbarState.dir === "ltr"}
          onClick={toggleDirection}
        >
          LTR
        </ToolbarButton>
        <span className="rich-text-toolbar-sep" />
        <ToolbarButton
          label="افزودن تصویر"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? "…" : "🖼"}
        </ToolbarButton>
        {toolbarState.image && (
          <>
            <ToolbarButton
              label="تصویر در راست (متن از چپ دور آن بچرخد)"
              active={toolbarState.imageAlign === "right"}
              onClick={() => setImageAlign("right")}
            >
              ⇥
            </ToolbarButton>
            <ToolbarButton
              label="تصویر در وسط"
              active={toolbarState.imageAlign === "center"}
              onClick={() => setImageAlign("center")}
            >
              ▭
            </ToolbarButton>
            <ToolbarButton
              label="تصویر در چپ (متن از راست دور آن بچرخد)"
              active={toolbarState.imageAlign === "left"}
              onClick={() => setImageAlign("left")}
            >
              ⇤
            </ToolbarButton>
            <ToolbarButton
              label="زیرنویس تصویر"
              active={!!toolbarState.imageCaption}
              onClick={setImageCaption}
            >
              زیرنویس
            </ToolbarButton>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleImageChosen}
          style={{ display: "none" }}
        />
        <span className="rich-text-toolbar-sep" />
        <ToolbarButton
          label="واگرد"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!toolbarState.canUndo}
        >
          ↺
        </ToolbarButton>
        <ToolbarButton
          label="ازنو"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!toolbarState.canRedo}
        >
          ↻
        </ToolbarButton>
      </div>
      {uploadError && <p className="error rich-text-upload-error">{uploadError}</p>}
      <EditorContent editor={editor} className="rich-text-editor-content" />
    </div>
  );
}

export default RichTextEditor;
